/**
 * Générateur — Attendu 958 — « Définir et tracer les hauteurs dans un triangle. »
 * (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Triangles
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/958.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Définition de référence, écrite partout sous la même forme : « La hauteur
 *  issue d'un sommet d'un triangle est la DROITE qui passe par ce sommet et qui
 *  est perpendiculaire au côté opposé. » Une droite, jamais un segment, jamais
 *  une longueur (`definition_de_reference`, `objets_geometriques` 1).
 *
 *  1. nommer      — niveau 1 : la définition, au milieu de celles des quatre
 *                   droites remarquables (QCM ; phrase à trous qui fait écrire
 *                   la définition et les deux désignations).
 *  2. reconnaitre — niveau 2 : reconnaître la droite tracée sur une figure
 *                   CODÉE, en concurrence avec la médiatrice, la médiane et la
 *                   bissectrice (QCM sur figure ; V/F de définitions).
 *  3. tracer      — niveau 3 : tracer la hauteur dans un triangle acutangle —
 *                   le pied est une tâche à part, notée séparément.
 *  4. exterieure  — niveau 4 : triangle obtusangle, le côté est à PROLONGER,
 *                   le pied tombe hors du côté.
 *  5. isocele     — niveau 5 : triangle isocèle, hauteur issue du sommet
 *                   principal (construction) et ce qu'elle a de particulier (QCM).
 *  6. rectangle   — niveau 6 : triangle rectangle, hauteur relative à
 *                   l'hypoténuse (construction) et les deux hauteurs portées par
 *                   les côtés de l'angle droit (QCM).
 *  7. concours    — niveau 7 : les trois hauteurs et leur point de concours.
 *  Niveau 8 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - longueur du côté de référence : entier de 6 à 10 unités de figure ;
 *   - orientation du triangle : entier multiple de 10 dans [-170 ; 180], et
 *     jamais une orientation qui rendrait un côté horizontal ou vertical
 *     (exclusion 3, contrôlée à 4 degrés près sur les trois côtés) ;
 *   - position du troisième sommet : catalogue fermé de positions relatives, un
 *     catalogue par famille (acutangle, obtusangle, isocèle, rectangle) ;
 *   - angle minimal d'un triangle acutangle : 25 degrés (exclusion 1) ;
 *   - angle obtus d'un triangle obtusangle : au moins 100 degrés (exclusion 2) ;
 *   - proportion de triangles particuliers : 2 constructions sur 5 hors mélange,
 *     et 0,29 des tirages de `generate()` sans clé (voir § Non tranché) ;
 *   - nombre de hauteurs d'un triangle : 3, dit explicitement (V/F et QCM) ;
 *   - tolérance de perpendicularité : le pied est calculé exactement, la
 *     comparaison porte sur la position des deux extrémités du tracé ;
 *   - tolérance de passage par un point : 12 px, soit 0,4 carreau (voir § Non
 *     tranché), annoncée dans chaque consigne.
 *  Exclusions : aucun angle sous 25 degrés ; aucun triangle « obtusangle » dont
 *   l'angle obtus n'atteint pas 100 degrés (le catalogue est filtré à la
 *   construction du module, aucune figure n'est délivrée sous une étiquette
 *   qu'elle ne mérite pas) ; aucun côté horizontal ni vertical ; aucune
 *   validation par coefficient directeur et ordonnée à l'origine (le scoring
 *   compare des positions de points) ; le mot « orthocentre » n'apparaît nulle
 *   part, seul « point de concours des trois hauteurs » est employé.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. confondre la hauteur et la médiatrice
 *      → `hauteur_pour_mediatrice` (reconnaitre_qcm_unique, nommer_qcm_unique,
 *        reconnaitre_tableau_vf)
 *   2. identifier la hauteur comme le trait vertical
 *      → `hauteur_verticale` (reconnaitre_tableau_vf ; aucune figure n'a de côté
 *        horizontal ou vertical)
 *   3. croire qu'une hauteur coupe nécessairement le côté opposé
 *      → `pied_toujours_interieur` (reconnaitre_tableau_vf ; `erreurVisee` de
 *        exterieure_programme_construction)
 *   4. tracer une droite qui passe à peu près par le sommet
 *      → `trace_a_vue` (`erreurVisee` des constructions : les deux extrémités
 *        du tracé sont comparées, la tolérance est annoncée)
 *   5. tenir la hauteur pour achevée sans que son pied existe
 *      → `pied_absent` (reconnaitre_tableau_vf ; le pied est une tâche à part
 *        dans chaque construction)
 *   6. attribuer à la hauteur la définition de la médiatrice ou de la médiane
 *      → `definition_mediane` (nommer_qcm_unique, reconnaitre_qcm_unique)
 *   7. dans un triangle isocèle, croire qu'il n'y a qu'une hauteur
 *      → `une_seule_hauteur_isocele` (isocele_qcm_unique)
 *   8. dans un triangle rectangle, ne pas reconnaître les côtés de l'angle droit
 *      comme des hauteurs → `cotes_non_hauteurs` (rectangle_qcm_unique)
 *   9. croire que « hauteur issue de A » et « hauteur relative à [BC] » sont
 *      deux droites → `deux_designations` (reconnaitre_tableau_vf ;
 *      nommer_texte_trou_drag fait écrire les deux désignations)
 *
 *  Graduation → cartes : 1 nommer · 2 reconnaitre · 3 tracer · 4 exterieure ·
 *   5 isocele · 6 rectangle · 7 concours · 8 `generate()` sans clé.
 *   Réglage transverse « hauteur issue du sommet / relative au côté » : les deux
 *   désignations alternent dans les énoncés de toutes les cartes.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `tolerance_de_trace_sur_le_passage_par_un_point` = 0,2 cm : le scoring
 *     compare à 12 px fixes et le carreau du composant vaut 30 px ; la tolérance
 *     effective est de 0,4 carreau, annoncée dans les consignes.
 *   - Le réglage « papier à petits carreaux, réglure, feuille blanche » et la
 *     sortie papier avec grille d'auto-contrôle relèvent du rendu.
 *   - Le score partiel sur trois critères est obtenu en faisant du pied une
 *     tâche séparée ; la pondération des tâches relève du scoring, non touché.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeTexteTrou, makeProgrammeConstruction,
} from '../../../utils';
import {
    creerFigure, cadreur, imgElement, milieu, pied, dist,
    codageDroitInterieur, marqueInterieur,
} from './svgTriangles';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 958;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_NOMMER = 'Nommer une hauteur';
const L_RECONNAITRE = 'Reconnaître une hauteur';
const L_TRACER = 'Tracer une hauteur';
const L_EXT = 'Hauteur extérieure : prolonger le côté';
const L_ISO = 'Les hauteurs d\'un triangle isocèle';
const L_RECT = 'Les hauteurs d\'un triangle rectangle';
const L_CONCOURS = 'Les trois hauteurs et leur point de concours';

const COTE_MIN = 6;
const COTE_MAX = 10;
const ANGLE_MIN_ACUTANGLE = 25;
const ANGLE_MIN_OBTUS = 100;

const PX = 30;              // px par unité de figure (le carreau du composant)
const TOL = 12;             // px — tolérance FIXE du scoring (questionUtils)
const TOL_TXT = '0,4 carreau';
const PAD = 56;
const VB_FIG = { w: 380, h: 300 };
const COL_B = '#2563EB';
const COL_V = '#1D9E75';
const OR = '#b45309';
const ENCRE = '#334155';

const toRad = (d) => (d * Math.PI) / 180;
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const salt = (...n) => n.map((x) => Math.round(x)).join('_');

const E_MEDIATRICE = 'hauteur_pour_mediatrice';
const E_VERTICALE = 'hauteur_verticale';
const E_PIED_INT = 'pied_toujours_interieur';
const E_VUE = 'trace_a_vue';
const E_PIED_ABSENT = 'pied_absent';
const E_DEF_MEDIANE = 'definition_mediane';
const E_UNE_ISO = 'une_seule_hauteur_isocele';
const E_COTES_RECT = 'cotes_non_hauteurs';
const E_DESIGNATIONS = 'deux_designations';

/** Les deux désignations de la même droite (`deux_designations_de_la_hauteur`). */
const designation = (regime, nA, nB, nC) => (regime === 'sommet'
    ? `hauteur issue de ${nA}`
    : `hauteur relative au côté [${nB}${nC}]`);

const RAPPEL_TOL = (nom) => `<p><em>Le tracé est juste si chaque extrémité tombe à moins de `
    + `${TOL_TXT} de sa position. Le pied ${nom} est compté à part.</em></p>`;

// ═══════════════════════════════════════════════════════════
// GÉOMÉTRIE DES FIGURES
// ═══════════════════════════════════════════════════════════

/**
 * Catalogues fermés de positions du troisième sommet
 * (`position_du_troisieme_sommet`), en fractions de la longueur du côté [BC] :
 * B est l'origine, C est à (1 ; 0), A est à (fx ; fy). Chaque catalogue est
 * filtré à la construction du module par les contraintes d'angles de la spec —
 * un tirage n'a donc jamais à être rejoué ni « délivré tel quel » (vigilance 8).
 */
const CATALOGUE_ACUTANGLE = [
    { fx: 0.30, fy: 0.75 }, { fx: 0.35, fy: 0.95 }, { fx: 0.40, fy: 0.70 },
    { fx: 0.45, fy: 0.90 }, { fx: 0.50, fy: 0.65 }, { fx: 0.50, fy: 1.00 },
    { fx: 0.55, fy: 0.80 }, { fx: 0.60, fy: 0.95 }, { fx: 0.65, fy: 0.70 },
    { fx: 0.70, fy: 0.85 }, { fx: 0.60, fy: 0.60 }, { fx: 0.38, fy: 0.62 },
];
const CATALOGUE_OBTUSANGLE = [
    { fx: -0.60, fy: 0.80 }, { fx: -0.55, fy: 1.10 }, { fx: -0.45, fy: 0.85 },
    { fx: -0.40, fy: 1.10 }, { fx: -0.30, fy: 0.70 }, { fx: -0.25, fy: 0.85 },
    { fx: 1.60, fy: 0.80 }, { fx: 1.55, fy: 1.10 }, { fx: 1.45, fy: 0.85 },
    { fx: 1.40, fy: 1.10 }, { fx: 1.30, fy: 0.70 }, { fx: 1.25, fy: 0.85 },
];
const CATALOGUE_ISOCELE = [
    { fx: 0.5, fy: 0.60 }, { fx: 0.5, fy: 0.75 }, { fx: 0.5, fy: 0.90 },
    { fx: 0.5, fy: 1.05 }, { fx: 0.5, fy: 1.20 },
];
const CATALOGUE_RECTANGLE = [
    { fx: 0, fy: 0.60 }, { fx: 0, fy: 0.75 }, { fx: 0, fy: 0.90 },
    { fx: 0, fy: 1.05 }, { fx: 1, fy: 0.60 }, { fx: 1, fy: 0.75 },
    { fx: 1, fy: 0.90 }, { fx: 1, fy: 1.05 },
];

/** Les trois angles (degrés) du triangle B(0,0) C(1,0) A(fx,fy). */
const anglesDe = ({ fx, fy }) => {
    const B = { x: 0, y: 0 };
    const C = { x: 1, y: 0 };
    const A = { x: fx, y: fy };
    const a = dist(B, C);
    const b = dist(A, C);
    const c = dist(A, B);
    const ang = (x, y, z) => (Math.acos(Math.max(-1, Math.min(1, (y * y + z * z - x * x) / (2 * y * z)))) * 180) / Math.PI;
    return { A: ang(a, b, c), B: ang(b, a, c), C: ang(c, a, b) };
};

/** Abscisse du pied de la hauteur issue de A, en fraction de [BC]. */
const abscissePied = ({ fx }) => fx;

const acutangleOk = (p) => {
    const g = anglesDe(p);
    return Math.min(g.A, g.B, g.C) >= ANGLE_MIN_ACUTANGLE
        && Math.max(g.A, g.B, g.C) < 85          // aucun angle proche du droit : le pied reste loin des sommets
        && abscissePied(p) > 0.18 && abscissePied(p) < 0.82;
};
const obtusangleOk = (p) => {
    const g = anglesDe(p);
    const autres = [g.A, g.B, g.C].sort((x, y) => x - y);
    return autres[2] >= ANGLE_MIN_OBTUS && autres[0] >= ANGLE_MIN_ACUTANGLE
        && (abscissePied(p) < -0.15 || abscissePied(p) > 1.15);
};
const isoceleOk = (p) => Math.min(...Object.values(anglesDe(p))) >= ANGLE_MIN_ACUTANGLE;
const rectangleOk = (p) => {
    const g = anglesDe(p);
    return Math.min(g.A, g.B, g.C) >= ANGLE_MIN_ACUTANGLE
        && Math.abs(Math.max(g.A, g.B, g.C) - 90) < 0.001;
};

const ACUTANGLES = CATALOGUE_ACUTANGLE.filter(acutangleOk);
const OBTUSANGLES = CATALOGUE_OBTUSANGLE.filter(obtusangleOk);
const ISOCELES = CATALOGUE_ISOCELE.filter(isoceleOk);
const RECTANGLES = CATALOGUE_RECTANGLE.filter(rectangleOk);

/** Orientation : entier multiple de 10 dans [-170 ; 180]. */
const ORIENTATIONS = Array.from({ length: 36 }, (_, i) => -170 + 10 * i);

/**
 * Triangle posé : longueur du côté de référence, position catalogue, orientation.
 * Aucun côté à moins de 4 degrés d'une horizontale ou d'une verticale
 * (exclusion 3) ; l'orientation est reprise dans une liste bornée, jamais
 * au hasard indéfiniment.
 */
const poser = (positions) => {
    const cote = randInt(COTE_MIN, COTE_MAX);
    const pos = randomChoice(positions);
    const candidates = shuffle(ORIENTATIONS);
    const construire = (theta) => {
        const c = Math.cos(toRad(theta));
        const s = Math.sin(toRad(theta));
        const tr = (x, y) => ({ x: (x * c - y * s) * cote * PX, y: -(x * s + y * c) * cote * PX });
        return { B: tr(0, 0), C: tr(1, 0), A: tr(pos.fx, pos.fy), theta };
    };
    const obliques = (t) => [[t.A, t.B], [t.B, t.C], [t.C, t.A]].every(([P, Q]) => {
        const d = (Math.atan2(-(Q.y - P.y), Q.x - P.x) * 180) / Math.PI;
        const m = ((d % 90) + 90) % 90;
        return m > 4 && m < 86;
    });
    for (const theta of candidates) {
        const t = construire(theta);
        if (obliques(t)) return { ...t, cote, pos };
    }
    const t = construire(35);
    return { ...t, cote, pos };
};

// ═══════════════════════════════════════════════════════════
// ASSEMBLAGE D'UN PROGRAMME DE CONSTRUCTION
// ═══════════════════════════════════════════════════════════

const assembler = ({ points, preShapes, tasks, texte, label, key, params }) => {
    const xs = [];
    const ys = [];
    const noter = (p) => { xs.push(p.x); ys.push(p.y); };
    points.forEach(noter);
    tasks.forEach((t) => {
        const s = t.shape;
        if (s.type === 'segment' || s.type === 'line') { noter(s.p1); noter(s.p2); }
        else { xs.push(s.cx - s.r, s.cx + s.r); ys.push(s.cy - s.r, s.cy + s.r); }
    });
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const dx = PAD - minX;
    const dy = PAD - minY;
    const vb = {
        w: Math.round(Math.max(...xs) - minX + 2 * PAD),
        h: Math.round(Math.max(...ys) - minY + 2 * PAD),
    };
    const a2 = (v) => Math.round(v * 100) / 100;
    const mv = (p) => ({ ...p, x: a2(p.x + dx), y: a2(p.y + dy) });
    const mvShape = (s) => ((s.type === 'segment' || s.type === 'line')
        ? { ...s, p1: mv(s.p1), p2: mv(s.p2) }
        : { ...s, cx: a2(s.cx + dx), cy: a2(s.cy + dy) });
    const payload = makeProgrammeConstruction({
        pxPerCm: PX,
        tolerance: TOL,
        viewBox: vb,
        points: points.map(mv),
        preShapes,
        tasks: tasks.map((t) => ({ ...t, shape: mvShape(t.shape) })),
    }, texte, opts(label));
    return meta(payload, key, params);
};

const tachePoint = (id, P, nom, consigne) => ({
    id, consigne, color: COL_V, pointLabel: nom,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: 0 },
});
const tacheSegment = (id, P, Q, consigne, color = COL_V) => ({
    id, kind: 'segment', consigne, color,
    shape: { type: 'segment', p1: P, p2: Q },
});

/** Le triangle pré-tracé et ses trois sommets nommés. */
const socle = (t, nA, nB, nC) => ({
    points: [{ name: nA, ...t.A }, { name: nB, ...t.B }, { name: nC, ...t.C }],
    preShapes: [
        { type: 'line', from: nA, to: nB },
        { type: 'line', from: nB, to: nC },
        { type: 'line', from: nC, to: nA },
    ],
});

const paramsFig = (t, niveau, famille, extra) => {
    const g = anglesDe(t.pos);
    return {
        niveau,
        famille,
        triangle_particulier: famille === 'isocele' || famille === 'rectangle',
        longueur_du_cote_de_reference: t.cote,
        orientation_du_triangle: t.theta,
        position_du_troisieme_sommet: [t.pos.fx, t.pos.fy],
        angles_du_triangle: [g.A, g.B, g.C].map((x) => Math.round(x * 10) / 10),
        nombre_de_hauteurs_d_un_triangle: 3,
        ...extra,
    };
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — nommer (niveau 1) : la définition
// ═══════════════════════════════════════════════════════════

const genNommerQcm = () => {
    const [nA, nB, nC] = pickLetters(3);
    const regime = randomChoice(['sommet', 'cote']);
    const options = [
        {
            choix: `La droite qui passe par ${nA} et qui est perpendiculaire à (${nB}${nC}).`,
            code: null,
        },
        {
            choix: `La droite perpendiculaire à (${nB}${nC}) qui passe par le milieu de [${nB}${nC}].`,
            code: E_MEDIATRICE,
        },
        {
            choix: `La droite qui passe par ${nA} et par le milieu de [${nB}${nC}].`,
            code: E_DEF_MEDIANE,
        },
        {
            choix: `La droite qui passe par ${nA} et qui partage l'angle de sommet ${nA} en deux angles égaux.`,
            code: E_DEF_MEDIANE,
        },
    ];
    const texte = `<p>Dans le triangle ${nA}${nB}${nC}, laquelle de ces droites est la `
        + `<b>${designation(regime, nA, nB, nC)}</b> ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_NOMMER));
    return meta(payload, 'nommer_qcm_unique', {
        niveau: 1,
        regime_de_designation: regime,
        nombre_de_hauteurs_d_un_triangle: 3,
        distracteurs: options.slice(1),
    });
};

const genNommerTrous = () => {
    const [nA, nB, nC] = pickLetters(3);
    const texte = '<p>Complète la définition, puis les deux façons de nommer la même droite.</p>';
    // Aucun « : » entouré d'espaces dans la phrase (le rendu couperait la ligne
    // en « libellé » et « contenu » au premier deux-points).
    const phrase = `La hauteur issue du sommet ${nA} du triangle ${nA}${nB}${nC} est la `
        + `{{droite}} qui passe par ${nA} et qui est {{perpendiculaire}} au côté opposé `
        + `[${nB}${nC}]. On l'appelle aussi la hauteur {{relative}} au côté [${nB}${nC}], `
        + `et ces deux noms désignent la {{même}} droite. `
        + `Un triangle a {{3}} hauteurs, une par sommet.`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_NOMMER),
        mode: 'drag',
        distracteurs: ['segment', 'parallèle', 'longueur', 'médiatrice', '2'],
        strict: false,
    });
    return meta(payload, 'nommer_texte_trou_drag', {
        niveau: 1,
        nombre_de_hauteurs_d_un_triangle: 3,
        erreurVisee: E_DESIGNATIONS,
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — reconnaitre (niveau 2) : figure codée, V/F de définitions
// ═══════════════════════════════════════════════════════════

const VF_LIGNES = [
    { t: 'Un triangle a trois hauteurs, une par sommet.', v: 0, code: null },
    { t: 'La hauteur d\'un triangle est une droite, pas une longueur.', v: 0, code: null },
    { t: 'Le pied d\'une hauteur peut se trouver en dehors du côté, sur son prolongement.', v: 0, code: null, groupe: 'pied_dehors' },
    { t: 'La hauteur issue de A et la hauteur relative au côté [BC] sont la même droite.', v: 0, code: null },
    { t: 'La hauteur d\'un triangle est toujours le trait vertical de la figure.', v: 1, code: E_VERTICALE },
    { t: 'La médiatrice de [BC] est la hauteur issue de A du triangle ABC.', v: 1, code: E_MEDIATRICE },
    { t: 'La droite qui joint A au milieu de [BC] est la hauteur issue de A.', v: 1, code: E_DEF_MEDIANE },
    { t: 'Une hauteur coupe toujours le côté opposé entre ses deux extrémités.', v: 1, code: E_PIED_INT },
    // Cette ligne disait « Une hauteur est tracée même si son pied n'apparaît pas sur
    // la figure », comptée FAUSSE — alors qu'elle est VRAIE : quand le pied tombe en
    // dehors du côté, il n'est effectivement pas sur la figure telle qu'elle est
    // tracée. Elle contredisait donc la ligne vraie « le pied peut se trouver en
    // dehors du côté, sur son prolongement », et les deux sortaient ensemble dans
    // 21 % des tirages (retour prof 2026-09-26). Reformulée pour porter vraiment
    // l'erreur visée : ce qui est faux, ce n'est pas l'endroit où tombe le pied,
    // c'est de croire qu'on peut se dispenser de le construire.
    { t: 'Il n\'est pas nécessaire de prolonger le côté pour placer le pied d\'une hauteur.', v: 1, code: E_PIED_ABSENT, groupe: 'pied_dehors' },
];

/**
 * Deux affirmations d'un même `groupe` ne sortent jamais ensemble : l'une donnerait
 * la réponse de l'autre. « Le pied peut se trouver en dehors du côté » (vraie) et
 * « il n'est pas nécessaire de prolonger le côté pour l'y placer » (fausse) disent
 * la même chose de deux façons ; les servir ensemble offre un point gratuit.
 */
const tirerLignesVf = () => {
    for (let essai = 0; essai < 40; essai++) {
        const vrais = shuffle(VF_LIGNES.filter((l) => l.v === 0)).slice(0, 2);
        const faux = shuffle(VF_LIGNES.filter((l) => l.v === 1)).slice(0, 2);
        const groupes = [...vrais, ...faux].map((l) => l.groupe).filter(Boolean);
        if (new Set(groupes).size === groupes.length) return { vrais, faux };
    }
    // Repli : on écarte du vivier faux ceux qui collideraient avec les vrais retenus.
    const vrais = shuffle(VF_LIGNES.filter((l) => l.v === 0)).slice(0, 2);
    const pris = new Set(vrais.map((l) => l.groupe).filter(Boolean));
    const faux = shuffle(VF_LIGNES.filter((l) => l.v === 1 && !pris.has(l.groupe))).slice(0, 2);
    return { vrais, faux };
};

const genReconnaitreVf = () => {
    const { vrais, faux } = tirerLignesVf();
    const lignes = shuffle([...vrais, ...faux]);
    const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(lignes.map((x) => x.t), lignes.map((x) => x.v), texte, opts(L_RECONNAITRE));
    return meta(payload, 'reconnaitre_tableau_vf', {
        niveau: 2,
        nombre_de_hauteurs_d_un_triangle: 3,
        distracteurs: faux.map((l) => ({ choix: l.t, code: l.code })),
    });
};

const DROITES = [
    { id: 'hauteur', nom: 'la hauteur issue de {A}', code: null },
    { id: 'mediatrice', nom: 'la médiatrice de [{B}{C}]', code: E_MEDIATRICE },
    { id: 'mediane', nom: 'la médiane issue de {A}', code: E_DEF_MEDIANE },
    { id: 'bissectrice', nom: 'la bissectrice de l\'angle de sommet {A}', code: E_DEF_MEDIANE },
];

/**
 * Figure d'une droite remarquable, avec SON codage et rien d'autre. Le cadrage
 * inclut les extrémités de la droite tracée — la médiatrice sort du triangle —
 * de sorte que rien n'est rogné (vigilance 11).
 */
const figDroite = (t, quelle, nA, nB, nC, s) => {
    const m0 = milieu(t.B, t.C);
    const l0 = dist(t.B, t.C) * 0.55;
    const u0 = { x: (t.C.x - t.B.x) / dist(t.B, t.C), y: (t.C.y - t.B.y) / dist(t.B, t.C) };
    const pts = [t.A, t.B, t.C];
    if (quelle === 'mediatrice') {
        pts.push({ x: m0.x - u0.y * l0, y: m0.y + u0.x * l0 }, { x: m0.x + u0.y * l0, y: m0.y - u0.x * l0 });
    }
    const cad = cadreur(pts, VB_FIG, 46);
    const A = cad(t.A);
    const B = cad(t.B);
    const C = cad(t.C);
    const fig = creerFigure(VB_FIG, s);
    fig.segment(A, B);
    fig.segment(B, C);
    fig.segment(C, A);
    const G = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
    const dir = (P) => (Math.atan2(-(P.y - G.y), P.x - G.x) * 180) / Math.PI;
    fig.point(A, nA, dir(A), 14);
    fig.point(B, nB, dir(B), 14);
    fig.point(C, nC, dir(C), 14);
    const M = milieu(B, C);
    const trace = (P, Q) => fig.brut(`<line x1="${P.x.toFixed(1)}" y1="${P.y.toFixed(1)}" x2="${Q.x.toFixed(1)}" y2="${Q.y.toFixed(1)}" stroke="${OR}" stroke-width="2"/>`);
    if (quelle === 'hauteur') {
        const H = pied(A, B, C);
        trace(A, H);
        codageDroitInterieur(fig, H, A, C, { couleur: OR });
    } else if (quelle === 'mediatrice') {
        // Perpendiculaire à (BC) au milieu M, prolongée de part et d'autre.
        const ux = (C.x - B.x) / dist(B, C);
        const uy = (C.y - B.y) / dist(B, C);
        const l = dist(B, C) * 0.55;
        const P1 = { x: M.x - uy * l, y: M.y + ux * l };
        const P2 = { x: M.x + uy * l, y: M.y - ux * l };
        trace(P1, P2);
        codageDroitInterieur(fig, M, P1, C, { couleur: OR });
        fig.codageLongueur(B, M, { couleur: ENCRE });
        fig.codageLongueur(M, C, { couleur: ENCRE });
    } else if (quelle === 'mediane') {
        trace(A, M);
        fig.codageLongueur(B, M, { couleur: ENCRE });
        fig.codageLongueur(M, C, { couleur: ENCRE });
    } else {
        // Bissectrice de l'angle de sommet A : elle coupe [BC] en D tel que BD/DC = AB/AC.
        const k = dist(A, B) / (dist(A, B) + dist(A, C));
        const D = { x: B.x + k * (C.x - B.x), y: B.y + k * (C.y - B.y) };
        trace(A, D);
        marqueInterieur(fig, A, B, D, '', { couleur: OR, r: 22 });
        marqueInterieur(fig, A, D, C, '', { couleur: OR, r: 28 });
    }
    return fig;
};

const genReconnaitreQcm = () => {
    const t = poser(ACUTANGLES);
    const [nA, nB, nC] = pickLetters(3);
    const quelle = randomChoice(DROITES);
    const nommer = (d) => d.nom.replace('{A}', nA).replace('{B}', nB).replace('{C}', nC);
    const options = DROITES.map((d) => ({ choix: nommer(d), code: d.id === quelle.id ? null : d.code }));
    const texte = '<p>Une seule droite remarquable est tracée sur cette figure, avec son codage. '
        + 'Laquelle est-ce ? (La réponse se lit dans le codage, pas dans la position de la droite.)</p>';
    const payload = makeQcm(options.map((o) => o.choix), nommer(quelle), texte, {
        ...opts(L_RECONNAITRE),
        elements_additionnels: imgElement(figDroite(t, quelle.id, nA, nB, nC, salt(t.cote, t.theta, randInt(0, 999))).render()),
    });
    return meta(payload, 'reconnaitre_qcm_unique', paramsFig(t, 2, 'acutangle', {
        droite_tracee: quelle.id,
        distracteurs: options.filter((o) => o.choix !== nommer(quelle)),
    }));
};

// ═══════════════════════════════════════════════════════════
// Cartes 3 à 7 — les constructions
// ═══════════════════════════════════════════════════════════

/**
 * Construction d'une hauteur : le pied est une tâche à part (vigilance 3 et
 * `ecarts_assumes` 3), et le tracé est comparé sur SES DEUX extrémités — le
 * sommet et le pied — donc sur deux invariants indépendants (vigilance 2), et
 * jamais par un coefficient directeur (vigilance 1, exclusion 5).
 */
const tachesHauteur = (S, F0, nS, nF, nOpp1, nOpp2, prolonger) => {
    const F = { x: F0.x, y: F0.y };
    const taches = [];
    if (prolonger) {
        taches.push(tacheSegment('prolongement', { x: prolonger.depuis.x, y: prolonger.depuis.y }, F,
            `Prolonge le côté [${nOpp1}${nOpp2}] au-delà de ${prolonger.nom}, jusqu'au pied de la perpendiculaire issue de ${nS}.`, COL_B));
    }
    taches.push(tachePoint('pied', F, nF,
        `Place le pied ${nF} de la hauteur : le point de la droite (${nOpp1}${nOpp2}) où la perpendiculaire issue de ${nS} la rencontre.`));
    taches.push(tacheSegment('hauteur', S, F,
        `Trace la hauteur issue de ${nS} : de ${nS} jusqu'à son pied ${nF}.`));
    return taches;
};

const enonceTracer = (regime, nA, nB, nC, nH, intro) => `<p>${intro} Trace la `
    + `<b>${designation(regime, nA, nB, nC)}</b> du triangle ${nA}${nB}${nC}, c'est-à-dire la droite `
    + `qui passe par ${nA} et qui est perpendiculaire à (${nB}${nC}). Son pied se nomme ${nH}.</p>`
    + RAPPEL_TOL(nH);

const genTracerPC = () => {
    const t = poser(ACUTANGLES);
    const [nA, nB, nC, nH] = pickLetters(4);
    const regime = randomChoice(['sommet', 'cote']);
    const F = pied(t.A, t.B, t.C);
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesHauteur(t.A, F, nA, nH, nB, nC, null),
        texte: enonceTracer(regime, nA, nB, nC, nH,
            'Le triangle n\'a aucun angle obtus, le pied tombe donc entre les deux sommets.'),
        label: L_TRACER,
        key: 'tracer_programme_construction',
        params: paramsFig(t, 3, 'acutangle', {
            regime_de_designation: regime,
            position_du_pied: 'intérieur au côté',
            invariants: ['passage_par_le_sommet', 'position_du_pied'],
            erreurVisee: E_VUE,
        }),
    });
};

const genExterieurePC = () => {
    const t = poser(OBTUSANGLES);
    const [nA, nB, nC, nH] = pickLetters(4);
    const regime = randomChoice(['sommet', 'cote']);
    const F = pied(t.A, t.B, t.C);
    const depuisB = F.t < 0;
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesHauteur(t.A, { x: F.x, y: F.y }, nA, nH, nB, nC, {
            depuis: depuisB ? t.B : t.C, nom: depuisB ? nB : nC,
        }),
        texte: enonceTracer(regime, nA, nB, nC, nH,
            `Le triangle a un angle obtus : le pied tombe <b>hors</b> du côté [${nB}${nC}]. `
            + 'Le côté est donc à prolonger, et ce prolongement fait partie de la construction.'),
        label: L_EXT,
        key: 'exterieure_programme_construction',
        params: paramsFig(t, 4, 'obtusangle', {
            regime_de_designation: regime,
            position_du_pied: 'extérieur sur le prolongement',
            invariants: ['prolongement_du_cote', 'passage_par_le_sommet', 'position_du_pied'],
            erreurVisee: E_PIED_INT,
        }),
    });
};

const genIsocelePC = () => {
    const t = poser(ISOCELES);
    const [nA, nB, nC, nH] = pickLetters(4);
    const regime = randomChoice(['sommet', 'cote']);
    const F = pied(t.A, t.B, t.C);
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesHauteur(t.A, { x: F.x, y: F.y }, nA, nH, nB, nC, null),
        texte: enonceTracer(regime, nA, nB, nC, nH,
            `Le triangle ${nA}${nB}${nC} est isocèle en ${nA}.`),
        label: L_ISO,
        key: 'isocele_programme_construction',
        params: paramsFig(t, 5, 'isocele', {
            regime_de_designation: regime,
            position_du_pied: 'intérieur au côté',
            invariants: ['passage_par_le_sommet', 'position_du_pied'],
            erreurVisee: E_VUE,
        }),
    });
};

const genIsoceleQcm = () => {
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        {
            choix: `Elle est aussi la médiatrice de [${nB}${nC}] et la médiane issue de ${nA} : les trois droites sont confondues.`,
            code: null,
        },
        {
            choix: `C'est la seule hauteur du triangle : les deux autres sommets n'en ont pas.`,
            code: E_UNE_ISO,
        },
        {
            choix: `Elle est la médiatrice de [${nB}${nC}], mais le triangle n'a pas de médiane.`,
            code: E_UNE_ISO,
        },
        {
            choix: `Elle est perpendiculaire à [${nA}${nB}] et passe par le milieu de [${nA}${nC}].`,
            code: E_DEF_MEDIANE,
        },
    ];
    const texte = `<p>Le triangle ${nA}${nB}${nC} est isocèle en ${nA}. Qu'a de particulier la hauteur `
        + `issue de ${nA} ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_ISO));
    return meta(payload, 'isocele_qcm_unique', {
        niveau: 5,
        famille: 'isocele',
        triangle_particulier: true,
        nombre_de_hauteurs_d_un_triangle: 3,
        distracteurs: options.slice(1),
    });
};

const genRectanglePC = () => {
    const t = poser(RECTANGLES);
    const [nA, nB, nC, nH] = pickLetters(4);
    // L'angle droit est en B (fx = 0) ou en C (fx = 1) : la hauteur non portée
    // par un côté est celle issue du sommet de l'angle droit, relative à
    // l'hypoténuse.
    const droitEnB = t.pos.fx === 0;
    const S = droitEnB ? t.B : t.C;
    const nS = droitEnB ? nB : nC;
    const P = t.A;
    const Q = droitEnB ? t.C : t.B;
    const nP = nA;
    const nQ = droitEnB ? nC : nB;
    const F = pied(S, P, Q);
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesHauteur(S, { x: F.x, y: F.y }, nS, nH, nP, nQ, null),
        texte: `<p>Le triangle ${nA}${nB}${nC} est rectangle en ${nS}. Deux de ses trois hauteurs sont `
            + `déjà là : ce sont les côtés de l'angle droit. La troisième est la hauteur issue de ${nS}, `
            + `relative à l'hypoténuse [${nP}${nQ}]. Trace-la. Son pied se nomme ${nH}.</p>`
            + RAPPEL_TOL(nH),
        label: L_RECT,
        key: 'rectangle_programme_construction',
        params: paramsFig(t, 6, 'rectangle', {
            position_du_pied: 'intérieur au côté',
            invariants: ['passage_par_le_sommet', 'position_du_pied'],
            erreurVisee: E_COTES_RECT,
        }),
    });
};

const genRectangleQcm = () => {
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        {
            choix: `Les côtés [${nB}${nA}] et [${nB}${nC}] de l'angle droit, et la perpendiculaire à [${nA}${nC}] passant par ${nB}.`,
            code: null,
        },
        {
            choix: `Seulement la perpendiculaire à [${nA}${nC}] passant par ${nB} : les côtés ne sont pas des hauteurs.`,
            code: E_COTES_RECT,
        },
        {
            choix: `Les trois médiatrices des côtés du triangle.`,
            code: E_MEDIATRICE,
        },
        {
            choix: `Les trois droites qui joignent chaque sommet au milieu du côté opposé.`,
            code: E_DEF_MEDIANE,
        },
    ];
    const texte = `<p>Le triangle ${nA}${nB}${nC} est rectangle en ${nB}. Quelles sont ses trois `
        + 'hauteurs ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_RECT));
    return meta(payload, 'rectangle_qcm_unique', {
        niveau: 6,
        famille: 'rectangle',
        triangle_particulier: true,
        nombre_de_hauteurs_d_un_triangle: 3,
        distracteurs: options.slice(1),
    });
};

/** Point de concours des trois hauteurs (jamais nommé « orthocentre », exclusion 6). */
const concours = (A, B, C) => {
    // Hauteur issue de A : (X - A) . (C - B) = 0 ; hauteur issue de B : (X - B) . (C - A) = 0.
    const a1 = C.x - B.x;
    const b1 = C.y - B.y;
    const c1 = A.x * a1 + A.y * b1;
    const a2 = C.x - A.x;
    const b2 = C.y - A.y;
    const c2 = B.x * a2 + B.y * b2;
    const det = a1 * b2 - b1 * a2;
    if (Math.abs(det) < 1e-9) return null;
    return { x: (c1 * b2 - b1 * c2) / det, y: (a1 * c2 - c1 * a2) / det };
};

const genConcoursPC = () => {
    const t = poser(ACUTANGLES);
    const [nA, nB, nC, nO] = pickLetters(4);
    const HA = pied(t.A, t.B, t.C);
    const HB = pied(t.B, t.A, t.C);
    const HC = pied(t.C, t.A, t.B);
    const O = concours(t.A, t.B, t.C) || { x: (t.A.x + t.B.x + t.C.x) / 3, y: (t.A.y + t.B.y + t.C.y) / 3 };
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: [
            tacheSegment('hauteur_A', t.A, { x: HA.x, y: HA.y }, `Trace la hauteur issue de ${nA}, jusqu'à son pied sur (${nB}${nC}).`),
            tacheSegment('hauteur_B', t.B, { x: HB.x, y: HB.y }, `Trace la hauteur issue de ${nB}, jusqu'à son pied sur (${nA}${nC}).`),
            tacheSegment('hauteur_C', t.C, { x: HC.x, y: HC.y }, `Trace la hauteur issue de ${nC}, jusqu'à son pied sur (${nA}${nB}).`),
            tachePoint('concours', O, nO, `Place le point ${nO} où les trois hauteurs se coupent.`),
        ],
        texte: `<p>Trace les <b>trois</b> hauteurs du triangle ${nA}${nB}${nC}, une par sommet, puis `
            + `place leur <b>point de concours</b> ${nO} : les trois hauteurs passent par un même point.</p>`
            + `<p><em>Chaque tracé est juste si ses deux extrémités tombent à moins de ${TOL_TXT} de leur `
            + 'position ; le point de concours est compté à part.</em></p>',
        label: L_CONCOURS,
        key: 'concours_programme_construction',
        params: paramsFig(t, 7, 'acutangle', {
            position_du_pied: 'intérieur au côté',
            invariants: ['hauteur_issue_de_A', 'hauteur_issue_de_B', 'hauteur_issue_de_C', 'point_de_concours'],
            erreurVisee: E_VUE,
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    nommer_qcm_unique: genNommerQcm,
    nommer_texte_trou_drag: genNommerTrous,
    reconnaitre_tableau_vf: genReconnaitreVf,
    reconnaitre_qcm_unique: genReconnaitreQcm,
    tracer_programme_construction: genTracerPC,
    exterieure_programme_construction: genExterieurePC,
    isocele_programme_construction: genIsocelePC,
    isocele_qcm_unique: genIsoceleQcm,
    rectangle_programme_construction: genRectanglePC,
    rectangle_qcm_unique: genRectangleQcm,
    concours_programme_construction: genConcoursPC,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_drag: 'Texte à trous',
    programme_construction: 'Construction',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nommer', label: L_NOMMER, formatsList: buildFormats('nommer') },
    { id: 'reconnaitre', label: L_RECONNAITRE, formatsList: buildFormats('reconnaitre') },
    { id: 'tracer', label: L_TRACER, formatsList: buildFormats('tracer') },
    { id: 'exterieure', label: L_EXT, formatsList: buildFormats('exterieure') },
    { id: 'isocele', label: L_ISO, formatsList: buildFormats('isocele') },
    { id: 'rectangle', label: L_RECT, formatsList: buildFormats('rectangle') },
    { id: 'concours', label: L_CONCOURS, formatsList: buildFormats('concours') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    ACUTANGLES, OBTUSANGLES, ISOCELES, RECTANGLES, ORIENTATIONS,
    anglesDe, poser, concours, VF_LIGNES, DROITES,
};
